import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { ScanToContext } from "..";
import { ScanToMobilePreview, AppelServeurPreviewData } from "../View";

import { PropsType, QuickOption } from "./types";
import { LANGUAGE_CODES, DEFAULT_QUICK_OPTIONS } from "./constants";
import { CUSTOM_ICON_NEW_KEY } from "../shared/constants";
import { Container, LeftSection, RightSection } from "./styled";
import { QuickOptionsCard } from "./QuickOptionsCard";
import { CustomMessageCard } from "./CustomMessageCard";
import { DeleteConfirmModal } from "./DeleteConfirmModal";

// ─── Component ──────────────────────────────────────────

export function AppelServeur({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: RootState) => state.global.infoUser.maincolor
    );

    const {
        sharedBannerData,
        sharedOffresData,
        sharedActionsData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedDiversData,
        sharedPaiementData,
        setSharedAppelServeurData,
    } = React.useContext(ScanToContext);

    // ─── Languages ───────────────────────────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── State ──────────────────────────────────────────
    const savedServer = useSelector(
        (state: RootState) => state.scanTo?.serverCallOptions
    );
    const [serverCallEnabled, setServerCallEnabled] = React.useState(
        savedServer?.serverCallEnabled ?? true
    );
    const [selectedLang, setSelectedLang] = React.useState(
        savedServer?.selectedLang || "fr"
    );
    const [quickOptions, setQuickOptions] = React.useState<QuickOption[]>(
        () =>
            savedServer?.fullQuickOptions ||
            JSON.parse(JSON.stringify(DEFAULT_QUICK_OPTIONS))
    );
    const [customMessageEnabled, setCustomMessageEnabled] = React.useState(
        savedServer?.customMessageEnabled ?? true
    );

    // Add option form state
    const [showAddOption, setShowAddOption] = React.useState(false);
    const [newOptionIcon, setNewOptionIcon] = React.useState("restaurant");
    const [newOptionTitle, setNewOptionTitle] = React.useState("");
    // Multi-upload : the picker keeps every icon the user uploads during
    // this add-option session (Offres uses the same pattern via
    // useMultiCustomIcons). The selected idx tracks which custom is
    // active when newOptionIcon === CUSTOM_ICON_NEW_KEY.
    const [newCustomIconUrls, setNewCustomIconUrls] = React.useState<string[]>(
        []
    );
    const [
        newCustomIconSelectedIdx,
        setNewCustomIconSelectedIdx,
    ] = React.useState<number>(0);

    // Delete confirmation state
    const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(
        null
    );
    const deleteOption = deleteConfirmId
        ? quickOptions.find((o) => o.id === deleteConfirmId)
        : null;

    // File input ref
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const previewVisible = true;

    // Auto-disable global toggle when all individual options are disabled
    const allOptionsDisabled =
        quickOptions.length > 0 && quickOptions.every((o) => !o.enabled);
    const allDisabled = allOptionsDisabled && !customMessageEnabled;
    const effectiveServerCallEnabled = serverCallEnabled && !allDisabled;

    // ─── Preview data builder ───────────────────────────
    const buildPreviewData = React.useCallback((): AppelServeurPreviewData => {
        return {
            serverCallEnabled: effectiveServerCallEnabled,
            quickOptions: quickOptions.map((opt) => ({
                id: opt.id,
                icon: opt.icon,
                customIconUrl: opt.customIconUrl,
                title: opt.title[selectedLang] || opt.title.fr || "",
                enabled: opt.enabled,
            })),
            customMessageEnabled,
        };
    }, [
        effectiveServerCallEnabled,
        quickOptions,
        selectedLang,
        customMessageEnabled,
    ]);

    // ─── Sync preview data ──────────────────────────────
    React.useEffect(() => {
        const previewData = {
            ...buildPreviewData(),
            // Full config for Redux persistence
            selectedLang,
            fullQuickOptions: quickOptions,
        };
        setSharedAppelServeurData(previewData);

        try {
            const channel = new BroadcastChannel("scan-to-preview-channel");
            channel.postMessage({ source: "appelServeur", ...previewData });
            channel.close();
        } catch (e) {
            // BroadcastChannel not supported
        }

        try {
            sessionStorage.setItem(
                "scanToAppelServeurPreviewData",
                JSON.stringify(previewData)
            );
        } catch (e) {
            // sessionStorage quota exceeded
        }
    }, [
        buildPreviewData,
        setSharedAppelServeurData,
        quickOptions,
        selectedLang,
    ]);

    // ─── Handlers ────────────────────────────────────────
    const handleToggleServerCall = () => {
        if (!effectiveServerCallEnabled) {
            // Re-activating: enable first option, or custom message if list empty
            if (quickOptions.length > 0) {
                setQuickOptions((prev) =>
                    prev.map((o, i) => (i === 0 ? { ...o, enabled: true } : o))
                );
            } else {
                setCustomMessageEnabled(true);
            }
        }
        setServerCallEnabled(!serverCallEnabled);
    };

    const handleToggleOption = (id: string) => {
        setQuickOptions((prev) =>
            prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o))
        );
    };

    const handleDeleteOption = (id: string) => {
        setDeleteConfirmId(id);
    };

    const confirmDelete = () => {
        if (deleteConfirmId) {
            const remaining = quickOptions.filter(
                (o) => o.id !== deleteConfirmId
            );
            setQuickOptions(remaining);
            setDeleteConfirmId(null);
            // If no enabled options remain and global is on, auto-enable custom message
            if (
                serverCallEnabled &&
                (remaining.length === 0 || remaining.every((o) => !o.enabled))
            ) {
                setCustomMessageEnabled(true);
            }
        }
    };

    const handleOptionTitleChange = (id: string, value: string) => {
        setQuickOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? { ...o, title: { ...o.title, [selectedLang]: value } }
                    : o
            )
        );
    };

    const handleResetOptionTitle = (id: string) => {
        const def = DEFAULT_QUICK_OPTIONS.find((d) => d.id === id);
        if (!def) return;
        setQuickOptions((prev) =>
            prev.map((o) =>
                o.id === id
                    ? {
                          ...o,
                          title: {
                              ...o.title,
                              [selectedLang]:
                                  def.title[selectedLang] || def.title.fr,
                          },
                      }
                    : o
            )
        );
    };

    const handleResetAllOptions = () => {
        setQuickOptions(JSON.parse(JSON.stringify(DEFAULT_QUICK_OPTIONS)));
    };

    const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            // Just append to the list — do NOT auto-select the freshly
            // uploaded icon. The user keeps whatever they had picked
            // (default "restaurant" on first open), and clicks on the
            // new thumbnail explicitly if they want to use it. This
            // mirrors the user's expectation that "uploading" only
            // *adds* to the available choices.
            setNewCustomIconUrls((prev) => [...prev, dataUrl]);
        };
        reader.readAsDataURL(file);
        // Reset input so the same file can be re-selected
        e.target.value = "";
    };

    const handleAddOption = () => {
        if (!newOptionTitle.trim()) return;
        const newId = `custom_${Date.now()}`;
        const initialTitle = { [selectedLang]: newOptionTitle.trim() } as Record<
            string,
            string
        >;
        const newOption: QuickOption = {
            id: newId,
            icon:
                newOptionIcon === CUSTOM_ICON_NEW_KEY ? "help" : newOptionIcon,
            customIconUrl:
                newOptionIcon === CUSTOM_ICON_NEW_KEY
                    ? newCustomIconUrls[newCustomIconSelectedIdx] || undefined
                    : undefined,
            title: initialTitle,
            // Snapshot at creation — feeds the read-only "Default text"
            // column. Built-in options read their default from
            // defaultQuickOptions; custom ones don't have a ship-default
            // to point at, so we freeze the creation-time value here.
            defaultTitle: { ...initialTitle },
            isDefault: false,
            enabled: true,
        };
        setQuickOptions((prev) => [...prev, newOption]);
        setNewOptionTitle("");
        setNewOptionIcon("restaurant");
        setNewCustomIconUrls([]);
        setNewCustomIconSelectedIdx(0);
        setShowAddOption(false);
    };

    return (
        <Container data-testid="scan-to-server-call-container">
            <LeftSection data-testid="scan-to-server-call-left-section">
                <QuickOptionsCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLang={selectedLang}
                    onSelectLang={setSelectedLang}
                    quickOptions={quickOptions}
                    defaultQuickOptions={DEFAULT_QUICK_OPTIONS}
                    serverCallEnabled={serverCallEnabled}
                    effectiveServerCallEnabled={effectiveServerCallEnabled}
                    onToggleServerCall={handleToggleServerCall}
                    onToggleOption={handleToggleOption}
                    onDeleteOption={handleDeleteOption}
                    onOptionTitleChange={handleOptionTitleChange}
                    onResetOptionTitle={handleResetOptionTitle}
                    onResetAllOptions={handleResetAllOptions}
                    showAddOption={showAddOption}
                    setShowAddOption={setShowAddOption}
                    newOptionIcon={newOptionIcon}
                    setNewOptionIcon={setNewOptionIcon}
                    newOptionTitle={newOptionTitle}
                    setNewOptionTitle={setNewOptionTitle}
                    newCustomIconUrls={newCustomIconUrls}
                    newCustomIconSelectedIdx={newCustomIconSelectedIdx}
                    setNewCustomIconSelectedIdx={setNewCustomIconSelectedIdx}
                    fileInputRef={fileInputRef}
                    onIconUpload={handleIconUpload}
                    onAddOption={handleAddOption}
                />

                <CustomMessageCard
                    mainColor={mainColor}
                    customMessageEnabled={customMessageEnabled}
                    onToggle={() =>
                        setCustomMessageEnabled(!customMessageEnabled)
                    }
                />
            </LeftSection>

            {/* ─── Right Section: Preview ─── */}
            <RightSection
                data-testid="scan-to-server-call-preview-section"
                isVisible={previewVisible}
            >
                <ScanToMobilePreview
                    mainColor={mainColor}
                    selectedLanguage={selectedLang}
                    bannerData={sharedBannerData}
                    offresData={sharedOffresData}
                    actionsData={sharedActionsData}
                    publicitesData={sharedPublicitesData}
                    commandeData={sharedCommandeData}
                    diversData={sharedDiversData}
                    paiementData={sharedPaiementData}
                    appelServeurData={buildPreviewData()}
                    showServerCallPopup={true}
                />
            </RightSection>

            {/* ─── Delete Confirmation Popup ─── */}
            {deleteConfirmId && deleteOption && (
                <DeleteConfirmModal
                    deleteOption={deleteOption}
                    selectedLang={selectedLang}
                    onCancel={() => setDeleteConfirmId(null)}
                    onConfirm={confirmDelete}
                />
            )}
        </Container>
    );
}
